// feedUtils.js
'use strict';
import { LRUCache } from 'lru-cache';

import {
  FeedNameType,
  SUPPORTED_FEED_QUERIES,
} from '../resource/feedConstants.js';

const cache = new LRUCache({ max: 256 });

/**
 * Sort by alphabet ascending
 * @param {string} a - a item
 * @param {string} b - b item
 */
export const sortByAlphabetAscending = (a, b) => (b > a ? -1 : 1);

/**
 * Get sorted queries
 * @param {array} {filters} - feed filters.
 * @param {array} {sorts} - feed sorts.
 * @param {number} {page} - page number.
 * @param {number} {limit} - page size.
 * @param {number} {timestamp} - timestamp.
 * @param {number|undefined} {noRedirect} - no redirect.
 * @return {array} Return sorted queries
 */
export const getSortedQueries = ({
  filters = [],
  sorts = [],
  page,
  limit,
  timestamp,
  noRedirect,
} = {}) => {
  const querySet = new Set([
    ...filters.map(filter => `filters=${filter}`),
    ...sorts.map(sort => `sorting=${sort}`),
  ]);
  if (page != null) {
    querySet.add(`page=${page}`);
  }
  if (limit != null) {
    querySet.add(`limit=${limit}`);
  }
  if (timestamp != null) {
    querySet.add(`_=${timestamp}`);
  }
  if (noRedirect != null) {
    querySet.add(`no-redirect=${noRedirect}`);
  }
  const queries = [...querySet].sort(sortByAlphabetAscending);
  return queries;
};

/**
 * Get sorted feed names
 * @param {string} {feed} - feed name or feed id.
 * @return {array} Return sorted feed name
 */
export const getSortedFeedNames = ({ feed = '' } = {}) => {
  const feedNameSet = new Set();
  const url = new URL(feed, 'https://swag.live');
  const alias = url.pathname.replace(/^\//, '');
  const search = url.search;
  alias
    .split('&')
    .filter(s => s)
    .forEach(feedName => feedNameSet.add(feedName));
  const names = [...feedNameSet].sort(sortByAlphabetAscending);
  // feed might include query string, add query string on last item
  if (search && names.length) {
    const lastIndex = names.length - 1;
    names[lastIndex] = `${names[lastIndex]}${search}`;
  }
  return names;
};

/**
 * Parse query options.
 * @param {array} {options} - selected query options.
 * @return {object} Return parsed option object.
 */
export const parseQueryOptions = ({ options = [] } = {}) => {
  const feedNameSet = new Set();
  const querySet = new Set();
  options
    .filter(option => option)
    .forEach(option => {
      const url = new URL(option, 'https://swag.live');
      const alias = url.pathname.replace(/^\//, '');
      const search = url.search;
      if (alias) {
        alias
          .split('&')
          .filter(feedName => feedName)
          .forEach(feedName => feedNameSet.add(feedName));
      }
      if (search) {
        [...url.searchParams.entries()].forEach(item => {
          const [key, value] = item;
          querySet.add(`${key}=${value}`);
        });
      }
    });
  const intersectedFeedNames = [...feedNameSet].sort(sortByAlphabetAscending);
  const joinedFeedName = intersectedFeedNames.join('&');
  const queries = [...querySet].sort(sortByAlphabetAscending);
  const result = {
    intersectedFeedName: joinedFeedName,
    intersectedFeedNames,
    filters: queries
      .map(item => {
        if (!item) return;
        const u = new URL(`?${item}`, 'https://swag.live');
        return (
          u.searchParams.get('filters') || u.searchParams.get('filters_v2')
        );
      })
      .filter(item => item),
    sorts: queries
      .map(item => {
        if (!item) return;
        const u = new URL(`?${item}`, 'https://swag.live');
        return u.searchParams.get('sorting');
      })
      .filter(item => item),
    queries,
    feedNameWithQuery: `${joinedFeedName}${
      queries.length ? `?${queries.join('&')}` : ''
    }`,
  };
  return result;
};

/**
 * Get feed type from feed.
 * @param {string} {feedName} - feed name.
 * @return {FeedNameType} Return feed type.
 */
export const getFeedNameType = ({ feedName = '' } = {}) =>
  /^(user|discover|leaderboard)(_|-)/i.test(feedName)
    ? FeedNameType.USERS
    : /^(hashtag)(_|-)/i.test(feedName)
      ? FeedNameType.HASHTAGS
      : /^(category)(_|-)/i.test(feedName)
        ? FeedNameType.CATEGORIES
        : FeedNameType.MESSAGES;

/**
 * Get category from category and location.search sorting and filters
 * @param {string} {category} - feed category.
 * @param {string} {locationSearch} - location search.
 * @return {string} The feed category.
 */
export const getFeedCategory = ({ category = '', search = '' } = {}) => {
  const searchParams = new URLSearchParams(search);
  [...searchParams.keys()].forEach(key => {
    if (!SUPPORTED_FEED_QUERIES.includes(key)) {
      searchParams.delete(key);
    }
  });
  const searchString = searchParams.toString();
  const cacheKey = `${category}:${searchString}`;
  let result = cache.get(cacheKey);
  if (!result) {
    result = decodeURIComponent(
      `${category}${searchString ? `?${searchString}` : ''}`
    );
    cache.set(cacheKey, result);
  }
  return result;
};

/**
 * 1. Check if the target category has all the feed alias from base category.
 * 2. The order of the arguments (probeCategory, targetCategory) matters:
 *    All the alias in first argument `probeCategory` would be confirmed to exist in the second argument `targetCategory`,
 *    but we don't promise the opposite.
 * @param {string} {probeCategory} - all the feed alias in this must be found in `targetCategory`
 * @param {string} {targetCategory} - the category we have to check if it contains all the `probeCategory`s alias
 * @return {bool} If all the feed alias in `probeCategory` can be found in `targetCategory`.
 */
export const isAllAliasesInTargetCategory = ({
  probeCategory,
  targetCategory,
}) => {
  const baseUrl = 'https://swag.live';
  const url1 = new URL(probeCategory, baseUrl);
  const url2 = new URL(targetCategory, baseUrl);

  const aliases1 = url1.pathname.slice(1).split('&');
  const aliases2 = url2.pathname.slice(1).split('&');

  return aliases1.every(alias => aliases2.includes(alias));
};
